/**
 * NotificationBell – reusable in-portal notification bell.
 * Listens in real-time to organizations/{orgId}/notifications
 * for the given recipientId (UID or 'ADMIN').
 *
 * Props:
 *   orgId        {string}  required
 *   recipientId  {string}  required – UID for member/volunteer; 'ADMIN' for admin portal
 *   accentColor  {string}  optional – badge colour, defaults to '#6B46C1'
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  subscribeToNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from '../../api/notificationsApi';

const DEFAULT_ACCENT = '#6B46C1';

function timeAgo(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60)   return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function NotificationBell({ orgId, recipientId, accentColor = DEFAULT_ACCENT }) {
  const [notifs, setNotifs]   = useState([]);
  const [open, setOpen]       = useState(false);
  const containerRef          = useRef(null);

  // Real-time listener
  useEffect(() => {
    if (!orgId || !recipientId) return;
    const unsub = subscribeToNotifications(orgId, recipientId, setNotifs);
    return unsub;
  }, [orgId, recipientId]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = () => setOpen(v => !v);

  const handleMarkRead = useCallback(async (notif) => {
    await markNotificationRead(orgId, notif.id);
  }, [orgId]);

  const handleMarkAllRead = useCallback(async () => {
    await markAllNotificationsRead(orgId, recipientId);
  }, [orgId, recipientId]);

  const unreadCount = notifs.length;

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-flex' }}>
      {/* Bell button */}
      <button
        onClick={handleOpen}
        title={`${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`}
        style={{
          position: 'relative',
          background: 'rgba(255,255,255,0.15)',
          border: '1px solid rgba(255,255,255,0.3)',
          borderRadius: 8,
          padding: '6px 10px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          color: '#fff',
          transition: 'background 0.15s',
        }}
      >
        {/* Bell icon */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {/* Unread badge */}
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: -4,
            right: -4,
            background: '#EF4444',
            color: '#fff',
            borderRadius: '50%',
            fontSize: 10,
            fontWeight: 700,
            width: 16,
            height: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
            border: '1.5px solid #fff',
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          right: 0,
          width: 340,
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          border: '1px solid #E5E7EB',
          zIndex: 9999,
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: '1px solid #F3F4F6',
            background: '#FAFAFA',
          }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
              Notifications {unreadCount > 0 && <span style={{ color: '#EF4444' }}>({unreadCount})</span>}
            </span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{ fontSize: 12, color: accentColor, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {notifs.length === 0 ? (
              <div style={{ padding: '28px 16px', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
                🔔 You're all caught up!
              </div>
            ) : (
              notifs.map(n => (
                <div
                  key={n.id}
                  onClick={() => handleMarkRead(n)}
                  style={{
                    display: 'flex',
                    gap: 10,
                    padding: '12px 16px',
                    borderBottom: '1px solid #F9FAFB',
                    cursor: 'pointer',
                    background: n.read ? '#fff' : '#F5F3FF',
                    transition: 'background 0.15s',
                  }}
                >
                  <div style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: n.read ? 'transparent' : accentColor,
                    flexShrink: 0,
                    marginTop: 6,
                  }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#111827', marginBottom: 2 }}>
                      {n.title}
                    </div>
                    <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.4 }}>
                      {n.message}
                    </div>
                    <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                      {timeAgo(n.created_at)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifs.length > 0 && (
            <div style={{
              padding: '10px 16px',
              borderTop: '1px solid #F3F4F6',
              background: '#FAFAFA',
              textAlign: 'center',
              fontSize: 12,
              color: '#9CA3AF',
            }}>
              Showing unread only · Click to dismiss
            </div>
          )}
        </div>
      )}
    </div>
  );
}
